import React, { useState, useRef, useEffect } from 'react';

interface Props {
  value: number[];
  onChange: (v: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const isIntegerString = (s: string) => /^\s*-?\d+\s*$/.test(s);
const isRangeString = (s: string) => /^\s*-?\d+\s*-\s*-?\d+\s*$/.test(s);

export const NumericChipsInput: React.FC<Props> = ({ value = [], onChange, placeholder = 'Add a priority and press Enter (or range like 1-20)', disabled = false }) => {
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // keep input placeholder or focus behavior if needed
  }, []);

  // Helper: split raw token string into parts
  const parseParts = (raw: string): string[] => raw.trim().split(/[,\s]+/).filter(Boolean);

  // Helper: push number into arr if not present in existing or arr
  const pushUnique = (arr: number[], n: number) => {
    if (!value.includes(n) && !arr.includes(n)) {arr.push(n);}
  };

  // Helper: expand a range token into numbers and push into arr
  const expandRange = (token: string, arr: number[]) => {
    const MAX_RANGE_SIZE = 500;
    const [rawA, rawB] = token.split('-');
    const a = Number(rawA.trim());
    const b = Number(rawB.trim());
    if (!Number.isFinite(a) || !Number.isFinite(b)) {return;}
    const start = Math.min(a, b);
    const end = Math.max(a, b);
    const len = end - start + 1;
    if (len > MAX_RANGE_SIZE) {
      return;
    }
    for (let v = start; v <= end; v++) {pushUnique(arr, v);}
  };

  const commitToken = (raw: string) => {
    const tok = raw.trim();
    if (!tok) {return;}
    const parts = parseParts(tok);
    const nums: number[] = [];
    for (const p of parts) {
      if (isIntegerString(p)) {
        pushUnique(nums, Number(p));
      } else if (isRangeString(p)) {
        expandRange(p, nums);
      } else {
        console.info(`[NumericChipsInput] Invalid token "${p}" ignored`);
      }
    }
    if (nums.length > 0) {onChange([...value, ...nums].sort((a, b) => a - b));}
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitToken(input);
      setInput('');
    } else if (e.key === 'Backspace' && input === '' && value.length > 0) {
      // remove last
      onChange(value.slice(0, -1));
    }
    else{
      console.info(`[NumericChipsInput] Key "${e.key}" pressed, current input: "${input}"`);
    }
  };

  const remove = (n: number) => onChange(value.filter(v => v !== n));

  return (
    <div className="numeric-chips-input">
      <div
        className="select2-input"
        onClick={() => inputRef.current?.focus()}
        role="group"
        onKeyDown={e => { if (e.key === 'Enter') {inputRef.current?.focus(); }}}
      >
        {value.map(n => (
          <span key={n} className="chip">
            <span>{n}</span>
            <button type="button" className="chip__remove" aria-label={`remove ${n}`} onClick={() => remove(n)}></button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => { commitToken(input); setInput(''); }}
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
  <small className="text-muted">Enter integers or ranges (e.g. 1-20). Separate with comma, space, or press Enter. Duplicates are ignored.</small>
    </div>
  );
};

export default NumericChipsInput;
